import { dbApiUrl, fetchDbApi } from "./dbApi";

/**
 * Salifert syringe dKH (matches `server/api/tests/kh_alk.php`): (1 − V) × 16, optional ×2 for low-resolution sample.
 * @param {number} syringeMlRemaining 0…1 (ml left in 1 ml syringe after titration)
 * @param {boolean} lowResolution 2 ml cell → multiply result by 2
 */
export function salifertSyringeDkh(syringeMlRemaining, lowResolution = false) {
  let dkh = (1 - syringeMlRemaining) * 16;
  if (lowResolution) dkh *= 2;
  return Math.round(dkh * 100) / 100;
}

/**
 * Drop titration: treat drop count as dKH (Salifert titration-style reading).
 */
export function salifertDropsDkh(drops) {
  return Math.round(Number(drops) * 100) / 100;
}

/**
 * GET `api/tests/catalog.php` — available Salifert test definitions.
 */
export async function fetchSalifertCatalog() {
  const res = await fetchDbApi(dbApiUrl("api/tests/catalog.php"), {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * GET `api/tests/list.php` — rows from `salifert_tests` (optionally filter by `test_code`).
 * @param {{ tankId?: string, testCode?: string, limit?: number }} opts
 */
export async function fetchSalifertTests(opts = {}) {
  const { tankId, testCode, limit = 100 } = opts;
  const params = new URLSearchParams({
    limit: String(Math.min(500, Math.max(1, limit))),
  });
  if (tankId) {
    params.set("tank_id", tankId);
  }
  if (testCode) {
    params.set("test_code", testCode);
  }
  const res = await fetchDbApi(
    `${dbApiUrl("api/tests/list.php")}?${params.toString()}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * KH/alk rows (legacy-compatible shape). Uses `api/tests/kh_alk.php`.
 * @param {{ tankId: string, limit?: number }} opts
 */
export async function fetchAlkTests(opts = {}) {
  const { tankId, limit = 50 } = opts;
  const params = new URLSearchParams({
    limit: String(Math.min(500, Math.max(1, limit))),
  });
  if (tankId) {
    params.set("tank_id", tankId);
  }
  const res = await fetchDbApi(
    `${dbApiUrl("api/tests/kh_alk.php")}?${params.toString()}`,
    {
      credentials: "include",
      headers: { Accept: "application/json" },
    },
  );
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * @param {{
 *   tankId: string,
 *   method: 'salifert_syringe' | 'salifert_drops',
 *   recordedAt?: string,
 *   syringeMlRemaining?: number,
 *   dropsCount?: number,
 *   lowResolution?: boolean,
 *   notes?: string
 * }} body
 */
export async function saveAlkTest(body) {
  const payload = {
    tank_id: body.tankId,
    method: body.method,
    recorded_at: body.recordedAt,
    low_resolution: !!body.lowResolution,
    notes: body.notes,
  };
  if (body.method === "salifert_syringe") {
    payload.syringe_ml_remaining = body.syringeMlRemaining;
  } else {
    payload.drops_count = body.dropsCount;
  }
  const res = await fetchDbApi(dbApiUrl("api/tests/kh_alk.php"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * Save a manual Salifert test row for a tank.
 * @param {{
 *   tankId: string,
 *   testCode: string,
 *   primaryValue: number|string,
 *   primaryUnit?: string,
 *   recordedAt?: string,
 *   notes?: string,
 *   inputs?: Record<string, unknown>
 * }} body
 */
export async function saveManualSalifertTest(body) {
  const payload = {
    tank_id: body.tankId,
    test_code: body.testCode,
    primary_value: body.primaryValue,
    primary_unit: body.primaryUnit,
    recorded_at: body.recordedAt,
    notes: body.notes,
    inputs: body.inputs,
  };
  const res = await fetchDbApi(dbApiUrl("api/tests/manual.php"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * Delete a saved Salifert test row.
 * @param {number|string} id
 */
export async function deleteSalifertTest(id) {
  const numericId = Number(id);
  if (!Number.isFinite(numericId) || numericId <= 0) {
    throw new Error("Invalid test id");
  }
  const res = await fetchDbApi(dbApiUrl("api/tests/list.php"), {
    method: "DELETE",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify({ id: numericId }),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * GET `api/tests/targets.php` — target values per tank/test.
 * @param {{ tankId?: string }} opts
 */
export async function fetchSalifertTargets(opts = {}) {
  const { tankId } = opts;
  const params = new URLSearchParams();
  if (tankId) {
    params.set("tank_id", tankId);
  }
  const suffix = params.toString() ? `?${params.toString()}` : "";
  const res = await fetchDbApi(`${dbApiUrl("api/tests/targets.php")}${suffix}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}

/**
 * POST `api/tests/targets.php` — upsert target values per tank/test.
 * @param {{ tankId: string, targets: Array<{test_code: string, target_value: number|string|null, target_unit?: string}> }} body
 */
export async function saveSalifertTargets(body) {
  const payload = {
    tank_id: body.tankId,
    targets: Array.isArray(body.targets) ? body.targets : [],
  };
  const res = await fetchDbApi(dbApiUrl("api/tests/targets.php"), {
    method: "POST",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(payload),
  });
  const text = await res.text();
  let json;
  try {
    json = JSON.parse(text);
  } catch {
    throw new Error(`Invalid JSON (${res.status}): ${text.slice(0, 200)}`);
  }
  if (!res.ok) {
    throw new Error(json.error || `HTTP ${res.status}`);
  }
  return json;
}
